'use client';

/**
 * PlaceShell — the frame every customer screen is rendered inside.
 *
 * TWO ZONES, NOT FOUR. The previous frame stacked header (56) + session ribbon
 * (34) + menu nav (up to three rows, ~92) and then closed with a fixed four-tab
 * rail (56) plus whatever bottom furniture the ordering island added on top of
 * it. On a 667px phone that was a fifth of the screen spent on chrome before a
 * single dish, with the guest's actual job — "you have 3 items, send them" —
 * rendered smaller than the navigation around it.
 *
 * Now:
 *   ┌────────────────────────────┐
 *   │ [logo] Place name  ›   ⌕ 👤│  header   56px + safe-area-top
 *   │        ● Table 7           │  status, only when it says something
 *   ├────────────────────────────┤
 *   │  All   Grills   Sides      │  nav, owned by the page, sticky under here
 *   │                            │
 *   │  children                  │  the food gets the rest
 *   │                            │
 *   │   ┌──────────────────────┐ │
 *   │   │ 3 items · ₵145  Review│ │  action bar — floats, and ONLY when the
 *   │   └──────────────────────┘ │  guest actually has something to do
 *   └────────────────────────────┘
 *
 * The bottom is the guest's ORDER, not navigation. Search and account moved to
 * the header; Book/Reserve is reached from the branch sheet and, on a place
 * whose whole business is bookings, from the action bar itself as a standing
 * invitation. See lib/customer/place-action.ts for that decision.
 */

import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'next-view-transitions';

import { cn } from '@/components/ui/utils';
import { useGuestOrderRecords } from '@/hooks/customer/useGuestOrderRecords';
import { PlaceHeader, placeHeaderHeight, type PlaceHeaderProps, type PlaceModeOption } from './PlaceHeader';
import { PlaceActionBar } from './PlaceActionBar';
import { usePlaceOptional } from './PlaceProvider';
import { BranchInfoSheet } from './BranchInfoSheet';
import { resolvePlaceAction, placeActionOccupiesSpace, type PlaceActionOption } from '@/lib/customer/place-action';
import type { PlaceState } from '@/lib/customer/place-state';
import type { PublicMenuBranchDetails } from '@/components/menu/PublicMenuShell';

export type PlaceShellProps = {
  basePath: string;
  experienceVersion?: 'v1' | 'v2';
  header: Omit<PlaceHeaderProps, 'status' | 'onStatusAction' | 'basePath'>;
  state: PlaceState | null;
  canvas?: string;
  chromeStyle?: CSSProperties;
  measure?: string;
  /** Branch details for the info sheet reached from the header title. */
  branchDetails?: PublicMenuBranchDetails;
  isOpenNow?: boolean | null;
  /** Enables the reviews block inside the branch sheet. */
  branchId?: string | null;
  businessName?: string | null;
  /** `/b/<menu-slug>` — the branch's own full page, linked from the sheet. */
  placePageHref?: string | null;
  /** Back to the branch chooser, when the brand has more than one place (doc 70 F2). */
  switchBranchHref?: string | null;
  /**
   * The unanswered "how do you want this?" question. Passed only while the
   * guest has items and has not answered — the bar renders it in place of the
   * cart summary, because a Review button that leads to a blocked checkout is
   * the dead end this shell exists to remove.
   */
  fulfilment?: { question: string; options: PlaceActionOption[] } | null;
  onChooseFulfilment?: (key: string) => void;
  /**
   * The fulfilment modes the guest can switch to, shown in the header's
   * status-line picker. Passed whenever the branch offers more than one way to
   * get food, so the guest can change modes before adding items — the action
   * bar only asks once the cart has something in it.
   */
  modes?: PlaceModeOption[] | null;
  onModeChange?: (key: string) => void;
  /**
   * The current fulfilment mode, rendered as the header's second line and made
   * tappable so the guest can switch modes. Passed whenever the branch offers
   * more than one way to get food AND the guest has picked one — this is the
   * persistent "change mode" control, independent of the action bar (which
   * only asks the question while the cart is empty).
   */
  modeSwitcher?: { label: string; detail?: string | null } | null;
  /** The guest's current mode key, for marking the active option in the picker. */
  currentModeKey?: string | null;
  /** A booking-led place's standing call to action (a caterer, an events venue). */
  invite?: { label: string; href: string; lead?: string | null } | null;
  /** Money formatted by the caller. Shown next to the item count. */
  cartTotal?: string | null;
  /** Extra clause on the cart line, e.g. "for pickup". */
  cartDetail?: string | null;
  /** "order" / "booking" / "quote", from the business-type vocabulary. */
  orderNoun?: string;
  /** Suppresses the action bar while a sheet that owns the screen is open. */
  barSuppressed?: boolean;
  /** Doc 03 line tabs, rendered under the header. Null for a single-line branch. */
  lineTabs?: ReactNode;
  children: ReactNode;
  className?: string;
};

export function PlaceShell({
  basePath,
  experienceVersion = 'v1',
  header,
  state,
  canvas,
  chromeStyle,
  measure = '48rem',
  branchDetails,
  isOpenNow,
  branchId,
  businessName,
  placePageHref,
  switchBranchHref,
  fulfilment,
  onChooseFulfilment,
  modes,
  onModeChange,
  currentModeKey,
  modeSwitcher,
  invite,
  cartTotal,
  cartDetail,
  orderNoun,
  barSuppressed = false,
  lineTabs,
  children,
  className,
}: PlaceShellProps) {
  const place = usePlaceOptional();
  const cartCount = place?.cart.count ?? 0;
  const liveState = place?.state ?? state;
  const openCart = place?.actions.openCart;
  const openOrders = place?.actions.openOrders;

  const [branchInfoOpen, setBranchInfoOpen] = useState(false);
  // Orders this device has placed, for the count on the header's Orders control.
  // Read here rather than in the header so the header stays presentational.
  const guestOrderCount = useGuestOrderRecords().length;

  /**
   * The cart total comes from two possible owners: the caller (which formats it
   * from its own currency knowledge) or the ordering island reporting up through
   * PlaceProvider. Prefer the island's — it is the one that changes as the guest
   * taps — and fall back to the caller's server-rendered value so the bar is
   * correct on first paint rather than flashing in.
   */
  const resolvedTotal = place?.cart.total ?? cartTotal ?? null;

  const action = useMemo(
    () =>
      liveState
        ? resolvePlaceAction({
            state: liveState,
            cartCount,
            cartTotal: [resolvedTotal, cartDetail].filter(Boolean).join(' · ') || null,
            fulfilment,
            invite,
            orderNoun,
          })
        : { kind: 'idle' as const },
    [liveState, cartCount, resolvedTotal, cartDetail, fulfilment, invite, orderNoun],
  );

  const barVisible = !barSuppressed && placeActionOccupiesSpace(action);

  const headerWithBranchInfo: PlaceHeaderProps = {
    ...header,
    status: liveState,
    basePath,
    onStatusAction: (act) => {
      if (act === 'openCart') openCart?.();
      if (act === 'openOrders') openOrders?.();
    },
    onBranchInfo: branchDetails ? () => setBranchInfoOpen(true) : header.onBranchInfo,
    // Only when the ordering island has registered an opener — a browse-only
    // branch has no order history, and a control that opens nothing is worse
    // than no control.
    onOrders: openOrders ?? header.onOrders,
    ordersBadge: guestOrderCount,
    chow: experienceVersion === 'v2',
    modes,
    onModeChange,
    currentModeKey,
    modeSwitcher,
  };

  return (
    <div
      data-customer-surface=""
      data-experience={experienceVersion}
      className={cn('relative flex min-h-dvh flex-col isolate', className)}
      style={{
        ['--place-measure' as string]: measure,
        // Published so sticky content beneath the header (the category nav) can
        // offset by the header's REAL height. The status line is conditional, so
        // a hardcoded offset is wrong roughly half the time.
        ['--place-header-height' as string]: placeHeaderHeight(liveState, modeSwitcher, experienceVersion === 'v2'),
        ...chromeStyle,
        ...(canvas ? { background: canvas } : null),
      }}
    >
      {canvas ? (
        <div
          aria-hidden="true"
          className="pointer-events-none fixed inset-0 -z-10"
          style={{ background: canvas }}
        />
      ) : null}

      <PlaceHeader {...headerWithBranchInfo} />

      {experienceVersion === 'v2' && liveState?.href &&
      (liveState.stance === 'live_order' || liveState.stance === 'awaiting_payment') ? (
        <Link
          href={liveState.href}
          className="chow-active-order"
          aria-label={`${liveState.stance === 'awaiting_payment' ? 'Pay for' : 'Track'} ${liveState.label}${liveState.detail ? `, ${liveState.detail}` : ''}`}
        >
          <span className="chow-active-order__copy">
            <span className="chow-active-order__title">{liveState.label}</span>
            {liveState.detail ? <span className="chow-active-order__detail">{liveState.detail}</span> : null}
          </span>
          <span className="chow-active-order__action">
            {liveState.stance === 'awaiting_payment' ? 'View order' : 'Track order'}
            <ArrowRight aria-hidden="true" className="size-4" />
          </span>
        </Link>
      ) : null}

      {/* Doc 03: what a guest can order from this branch. Renders nothing when the
          branch has one line, which is every branch today. */}
      {lineTabs}

      <main
        className="w-full flex-1"
        // Clearance only when the bar is actually there. The old shell reserved
        // 56px unconditionally for a rail that was always present, so every menu
        // ended with a band of empty space under its last dish.
        style={barVisible ? { paddingBottom: 'calc(5.5rem + env(safe-area-inset-bottom))' } : undefined}
      >
        {children}
      </main>

      {barVisible ? (
        <PlaceActionBar
          model={action}
          basePath={basePath}
          onOpenCart={openCart}
          onOpenOrders={openOrders}
          onChooseFulfilment={onChooseFulfilment}
          chow={experienceVersion === 'v2'}
        />
      ) : null}

      {branchDetails ? (
        <BranchInfoSheet
          open={branchInfoOpen}
          onOpenChange={setBranchInfoOpen}
          details={branchDetails}
          isOpenNow={isOpenNow}
          branchId={branchId}
          businessName={businessName}
          placePageHref={placePageHref}
          switchBranchHref={switchBranchHref}
        />
      ) : null}
    </div>
  );
}
